import * as THREE from "three";
import { Context } from "../core/context";
import { DbLayer, DbLine, FMathUtils, generateUUID } from "@/CadEngine";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "./IDrawOperation";

import { ULabel } from "@/ui/ULabel";
import {
  ActiveInputType,
  CommonColor,
  DrawDashLineParams,
  DrawLineParams,
} from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { BPMathUtils } from "@/utils/BPMathUtils";
import { BPLinear } from "@/core/BPLinear";
import { Layer } from "@/vendor/protobuf/packet2d";

class DrawOperationLine implements IDrawOperation {
  private m_context: Context;
  private m_size = { x: 1, y: 1, z: 1 };
  private m_locationObject: THREE.Line;
  private m_auxLine: THREE.Line | undefined;
  private m_returnData: DbLine[] = [];
  private m_TextLabel: ULabel;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_points: THREE.Vector3[] = [];
  private m_callbackUnderway: DrawLineParams["callbackUnderway"];
  private m_dimension: DimensionGroup;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_layer: DbLayer;
  private m_color: number;
  private m_isShift: Boolean = false;

  constructor(context: Context, acceptParams: DrawLineParams) {
    this.m_context = context;
    this.m_callbackUnderway = acceptParams.callbackUnderway;

    this.m_locationObject = this.m_context.ToolsManager.CreatedLocationLine();

    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_TextLabel.Visibility = true;
    this.m_dimension = new DimensionGroup(
      this.m_context.ViewManager,
      this.m_context.SceneManager.AuxModel
    );
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.m_layer = acceptParams.layer;
    this.m_color = acceptParams.color;
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {
    if (!this.m_context.SelectionManager.getEnableSnap) {
      return;
    }
    let tolSq = params.tol * params.tol;
    if (this.m_points.length > 0) {
      let start = new THREE.Vector3();
      let end = new THREE.Vector3();
      let target = new THREE.Vector3();
      let targetOnSegment = new THREE.Vector3();
      let point = this.m_points[this.m_points.length - 1];
      if (this.m_points.length > 1) {
        for (let index = 0; index < this.m_points.length - 1; index++) {
          start.copy(this.m_points[index]);
          end.copy(this.m_points[index + 1]);
          let distance = params.ray.distanceSqToSegment(
            start,
            end,
            target,
            targetOnSegment
          ); // target在射线上，targetOnSegment在线段上
          //  console.log(target.x,target.y,target.z+'          '+targetOnSegment.x,targetOnSegment.y,targetOnSegment.z)
          if (distance <= tolSq) {
            // let location = {x:Number(target.x.toFixed(3)), y:Number(target.y.toFixed(3)), z:Number(target.z.toFixed(3))};
            let location = {
              x: Number(targetOnSegment.x.toFixed(3)),
              y: Number(targetOnSegment.y.toFixed(3)),
              z: Number(targetOnSegment.z.toFixed(3)),
            };
            let center = new THREE.Vector3(
              (start.x + end.x) * 0.5,
              (start.y + end.y) * 0.5,
              (start.z + end.z) * 0.5
            );
            let d1 = start.distanceTo(targetOnSegment);
            let d2 = center.distanceTo(targetOnSegment);
            let d3 = end.distanceTo(targetOnSegment);
            let position = IndsertPosition.None;
            let min = Math.min(d1, d2, d3);
            let adjust = targetOnSegment.clone();
            if (min <= params.tol) {
              if (min == d1) {
                position = IndsertPosition.Start;
                adjust = start.clone();
              }
              if (min == d2) {
                position = IndsertPosition.Center;
                adjust = center;
              }
              if (min == d3) {
                position = IndsertPosition.End;
                adjust = end.clone();
              }
            }
            let insert = new IndserObject(location, distance, "", []);
            insert.adjust = adjust;
            insert.position = position;
            insert.geo = new THREE.Line3(start.clone(), end.clone());
            params.array.push(insert);
          }
        } // 以上先是用当前移动线检测是否和自己画的线段相交，不包括最后一个线段

        //夹角矫正
        {
          // 这段代码作用为用当前移动线段来和自己画的线的最后一段线检测是否相交
          // alert('yyyyyyyy')
          let direction = new THREE.Vector3();
          this.m_context.ViewManager.Camera.getWorldDirection(direction);
          let line = new THREE.Line3(
            this.m_points[this.m_points.length - 2].clone(),
            this.m_points[this.m_points.length - 1].clone()
          );
          let p0 = new THREE.Vector3();
          let p = params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
          let dir0 = line.end.clone().sub(line.start).normalize();
          let dir1 = p0.clone().sub(line.end).normalize();
          let rad = BPMathUtils.angleTo(dir0, dir1, direction);
          let angle = (rad * 180) / Math.PI;
          let orginLength = p0.distanceTo(line.end);
          let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2; // orginLength越小自动偏转幅度越大

          let offsetAngle = (angle + tolAngle) % 90;
          if (offsetAngle <= tolAngle * 2) {
            //五度之内视为垂直
            rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
            let p = dir0
              .applyAxisAngle(direction, rad)
              .multiplyScalar(p0.distanceTo(line.end));

            p = line.end.clone().add(dir0);
            let insert = new IndserObject(
              p,
              Math.sqrt(params.ray.distanceSqToPoint(p)),
              "",
              []
            );
            insert.adjust = p;
            insert.position = IndsertPosition.AuxOrthogonal;
            params.array.push(insert);
          }
        }
      }
      //正交矫正
      {
        // 这段代码作用为 当只有一个点的时候移动线段和 X轴的相交情况
        let direction = new THREE.Vector3();
        this.m_context.ViewManager.Camera.getWorldDirection(direction);
        let start = this.m_points[this.m_points.length - 1].clone();
        let p0 = new THREE.Vector3();
        params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
        let dir0 = new THREE.Vector3(1, 0, 0);
        let dir1 = p0.clone().sub(start).normalize();
        let rad = BPMathUtils.angleTo(dir0, dir1, direction);
        let angle = (rad * 180) / Math.PI;
        let orginLength = p0.distanceTo(start);
        let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2;
        let offsetAngle = (angle + tolAngle) % 90;

        if (offsetAngle <= tolAngle * 2) {
          //五度之内视为垂直
          rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
          let p = dir0
            .applyAxisAngle(direction, rad)
            .multiplyScalar(p0.distanceTo(start));
          p = start.clone().add(dir0);
          let insert = new IndserObject(
            p,
            Math.sqrt(params.ray.distanceSqToPoint(p)),
            "",
            []
          );
          insert.adjust = p;
          insert.position = IndsertPosition.AuxOrthogonal;
          // insert.geo = new THREE.Line3(start.clone(), end.clone());
          params.array.push(insert);
        }
      }
    }
    let ray = new THREE.Ray();

    params.array.forEach((item: any) => {
      if (item.position == "none") item.position = IndsertPosition.Insert;
      if (item.geo) {
        if (item.geo instanceof THREE.Line3) {
          let line = item.geo as THREE.Line3;
          let p0 = item.adjust ?? item.location;
          let v0 = line.start.clone().sub(line.end.clone());
          let dir0 = v0.clone().normalize();

          // 检测是否是圆心 start
          let dis = line.start.clone().distanceTo(line.end.clone());
          if (item.obejct?.length) {
            let insertObj = item.obejct[0];
            if (insertObj instanceof DbLine) {
              if (dis == 0 && insertObj.sphere?.radius == 1) {
                item.position = IndsertPosition.Start;
                item.adjust = insertObj.sphere?.center;
                return;
              }
            }
          }
          // 检测是否是圆心 end

          if (this.m_points.length > 0) {
            let point = this.m_points[this.m_points.length - 1];
            let v1 = new THREE.Vector3(
              p0.x - point.x,
              p0.y - point.y,
              p0.z - point.z
            );
            let offset = dir0.multiplyScalar(v1.dot(dir0));
            if (offset.lengthSq() <= params.tol * params.tol) {
              //正交矫正
              item.position = IndsertPosition.Vertical;
              item.adjust = new THREE.Vector3(
                p0.x - offset.x,
                p0.y - offset.y,
                p0.z - offset.z
              );
            }
          }
          ray.set(line.start, line.end.clone().sub(line.start).normalize()); // 这里开始检测移动线段是否和场景中两个或两个以上的交点处接触
          params.array.forEach((item1: any) => {
            if (item1.geo instanceof THREE.Line3) {
              let line1 = item1.geo as THREE.Line3;
              if (
                line != line1 &&
                line.start.clone().distanceTo(line1.start.clone()) >
                  0.0000000001 &&
                line.end.clone().distanceTo(line1.end.clone()) >
                  0.00000000001 &&
                line.start.y != line1.end.y
              ) {
                let target = new THREE.Vector3();
                let target1 = new THREE.Vector3();
                let distance = ray.distanceSqToSegment(
                  line1.start.clone(),
                  line1.end.clone(),
                  target
                );
                if (distance < 0.00000000001) {
                  line.closestPointToPoint(target, true, target1); //
                  if (target.distanceTo(target1) < 0.00000001) {
                    // 说明这两条线交叉了，因为这个点同时在两根线上
                    item.position = IndsertPosition.Cross;
                    item.adjust = target;
                    item.distance = target.distanceTo(
                      new THREE.Vector3(
                        item.location.x,
                        item.location.y,
                        item.location.z
                      )
                    );
                  }
                }
              }
            }
          });
        }
      }
    });
  }
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_context.Dom.style.cursor = "none";
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    let TextLabelLocation = new THREE.Vector3();
    TextLabelLocation.x = this.m_curPoint.x + this.Scale * 55;
    TextLabelLocation.y = this.m_curPoint.y - this.Scale * 10;
    this.m_TextLabel.SetVaule(
      "按住Ctrl+Z键将撤销回退到上一点,长按Shift锁定正交",
      TextLabelLocation,
      new THREE.Vector3(1, 0, 0)
    );
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      this.m_points.length
    ) {
      this.m_curPoint = this.m_context.ToolsManager.GetOrthogonalityPoint(
        this.m_curPoint,
        this.m_points[this.m_points.length - 1]
      );
    }
    this.updatedAuxLine();
    this.m_activeInputType = ActiveInputType.None;
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_inputValue = "";
  }
  updatedAuxLine() {
    if (this.m_points.length > 0) {
      this.m_dimension.Line.setVisible(true);
      this.m_dimension.Arc.setVisible(true);
      let linePoints: THREE.Vector3[] = [];
      for (let i = 0; i < this.m_points.length - 1; i++) {
        linePoints.push(this.m_points[i], this.m_points[i + 1]);
      }
      linePoints.push(this.m_points[this.m_points.length - 1], this.m_curPoint);
      this.m_auxLine?.geometry.setFromPoints(linePoints);
      this.m_auxLine?.computeLineDistances();
      this.updateDimension();
    } else {
      this.m_auxLine?.geometry.setFromPoints([]);
      this.m_dimension.Line.setVisible(false);
      this.m_dimension.Arc.setVisible(false);
    }
  }

  OnDownPoint(point: DrawOperationMovePointParams): void {
    if (!this.m_callbackUnderway(point, [...this.m_points, point.point])) {
      return;
    }
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      point.position == "none"
    ) {
      this.m_points.push(this.m_curPoint.clone());
    } else {
      this.m_points.push(point.point.clone());
    }
    if (this.m_points.length == 1) {
      this.m_auxLine = this.m_context.ToolsManager.CreatedLine(this.m_color);
    }

    // this.m_callbackUnderway(point.point.clone());
  }
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.keyCode == 16) {
      this.m_isShift = true;
    }
    if (e.key == "Escape") {
      this.m_context.DrawManager.DrawFinish(DrawOperateState.complete);
    }
    if (!this.m_points.length) return;
    this.m_angleInputValue = this.m_dimension.Arc.Input.Dom.value;
    this.m_distanceInputValue = this.m_dimension.Line.Input.Dom.value;
    if (e.ctrlKey && (e.key == "z"||e.key=="Z")) {
      this.m_points.pop();
      this.updatedAuxLine();
      this.m_context.ViewManager.Render();
    }

    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key === "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let angle = THREE.MathUtils.degToRad(parseFloat(this.m_angleInputValue));
      let dir = new THREE.Vector3(1, 0, 0);
      dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
      dir.multiplyScalar(length);
      this.m_curPoint = this.m_points[this.m_points.length - 1]
        .clone()
        .add(dir);
      this.OnDownPoint({
        point: this.m_curPoint,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.OnMovePoint({
        point: this.m_curPoint,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.m_context.ViewManager.Render();
    }
  }
  onKeyUp(e: KeyboardEvent): void {
    if (e.keyCode == 16) {
      this.m_isShift = false;
    }
  }
  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: DrawOperateState) {
    this.clear();
    if (operate == DrawOperateState.complete) {
      let ps: number[][] = [];
      for (let i = 0; i < this.m_points.length - 1; i++) {
        const point = this.m_points[i];
        const nextPoint = this.m_points[i + 1];
        ps.push([
          point.x,
          point.y,
          point.z,
          nextPoint.x,
          nextPoint.y,
          nextPoint.z,
        ]);
      }

      this.addLineData(ps);
      return this.m_returnData;
    } else if (operate == DrawOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {}

  onMouseUp(e: PointerEvent): void {}

  private updateDimension() {
    this.m_dimension.Line.SetDimension(
      this.m_points[this.m_points.length - 1],
      this.m_curPoint,
      this.Scale
    );
    this.m_dimension.Arc.SetDimension(
      this.m_points[this.m_points.length - 1],
      new THREE.Vector3(1, 0, 0),
      this.CurDir,
      this.Scale
    );
  }

  private addLineData(ps: number[][]) {
    let lines: DbLine[] = [];
    for (let i = 0; i < ps.length; i++) {
      let line = new DbLine({
        id: generateUUID(),
        layerId: this.m_layer.Id,
        blockId: "",
        points: new Float32Array(ps[i]),
        indexes: new Int32Array([0, 1]),
        color: this.m_color,
        extend_id: generateUUID(),
      });
      line.getBoundingBox();
      line.getBoundingSphere();
      lines.push(line);
    }
    let key = this.m_layer.Id + "_" + this.m_color;
    if (this.m_context.ModelManager.Lines[key]) {
      this.m_context.ModelManager.Lines[key].lines.push(...lines);
      this.m_context.ToolsManager.LinesToModel(
        this.m_context.ModelManager.Lines[key].lines,
        this.m_context.ModelManager.Lines[key].model
      );
    } else {
      this.m_context.ModelManager.Lines[key] = {
        canEdit: true,
        lines: lines,
        model: this.m_context.ModelManager.addLinesToModel(
          this.m_layer.Id,
          this.m_color!,
          lines
        ),
      };
    }
    this.m_returnData.push(...lines);
  }

  private clear() {
    this.m_dimension?.dispose();
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLine!,
    ]);
    this.m_TextLabel?.dispose();
  }
  private get CurDir() {
    return this.m_curPoint.clone().sub(this.m_points[this.m_points.length - 1]);
  }
  private get Scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
}

export { DrawOperationLine };
